import {Component, effect, input, OnInit, output, signal} from '@angular/core';
import {PlageSelectionnee} from '../../../models/plage-selectionnee.model';
import {Duree} from '../../../models/duree.model';
import {DateUtils, isNotNullOrUndefined} from 'sigale-angular';
import {StatutEvenement} from '../../../models/statut-evenement.model';
import {AuthService} from '../../../services/auth-service.service';
import {UtilisateurSigalett} from '../../../models/utilisateur-sigalett.model';
import {EvenementCalendrier} from '../model/evenement-calendrier.model';
import {TypeEvenementCalendrier} from '../model/type-evenement-calendrier.model';
import {Evenement} from '../model/evenement.model';
import {Reservation} from '../model/reservation.model';
import {TranslateService} from '@ngx-translate/core';
import {TypeEvenement} from '../../../models/type-evenement.model';

@Component({
	selector: 'app-journee-calendrier',
	templateUrl: './journee-calendrier.component.html',
	styleUrl: './journee-calendrier.component.scss'
})
export class JourneeCalendrierComponent implements OnInit {

	evenementsCalendriers = input.required<EvenementCalendrier[]>();
	date = input.required<Date>();
	estPlageSelectionnee = input.required<boolean>();
	masquerReservation = input.required<boolean>();

	notifierSelectionPlageDisponible = output<PlageSelectionnee>();
	notifierSelectionEvenement = output<Evenement>();
	notifierSelectionReservation = output<Reservation>();

	estSelectionnee = signal(false);

	utilisateurConnecte: UtilisateurSigalett;

	constructor(
		private authService: AuthService,
		private translateService: TranslateService) {
		effect(() => {
			const estPlageSelectionnee = this.estPlageSelectionnee();
			this.estSelectionnee.set(estPlageSelectionnee);
		}, {allowSignalWrites: true});
	}

	ngOnInit() {
		this.utilisateurConnecte = this.authService.getUtilisateurConnecte();
	}

	private estEvenementCalendrierSurJournee() {
		return this.evenementsCalendriers().some(evenementCalendrier => evenementCalendrier.estEvenementSurLaJournee());
	}

	/**
	 * True si la date n'est pas dépassé, qu'il n'y a pas d'évènement sur la journée et qu'il n'y pas d'évènement le matin et l'après-midi.
	 */
	plageSelectionnablePourNouvelEvenement(): boolean {
		const avantHier = new Date(new Date().getDate() - 2);
		return !DateUtils.isDayBefore(this.date(), avantHier) && !this.estEvenementCalendrierSurJournee() && this.evenementsCalendriers().length < 2;
	}

	/**
	 * Renvoi la durée disponible.
	 * Si on a aucun évènement ou réservation alors, on retourne une JOURNEE.
	 * Sinon, on retourne MATIN si on a un évènement ou une réservation l'après-midi.
	 * Sinon, on retourne APRES_MIDI si on a un évènement ou une réservation le matin.
	 */
	recupererDureeDisponible() {
		if (this.evenementsCalendriers().length === 0) {
			return Duree.JOURNEE;
		}

		return this.evenementsCalendriers()[0].duree.code === Duree.MATIN ? Duree.APRES_MIDI : Duree.MATIN;
	}

	/**
	 * Sélectionne ou déselectionne selon l'état, une plage disponible sur la journée.
	 */
	selectionnerDeselectionnerPlageDisponible(): void {
		this.estSelectionnee.set(!this.estSelectionnee());

		const duree = this.recupererDureeDisponible();

		this.notifierSelectionPlageDisponible.emit(<PlageSelectionnee>{
			dateSelectionnee: this.date(),
			duree: this.recupererDureeDisponible(),
			estSelectionne: this.estSelectionnee(),
			estDureeModifiable: duree === Duree.JOURNEE // Si c'est une journée alors on peut modifier en demi-journée sinon ça veut dire que la demi-journée est obligatoire.
		});
	}

	/**
	 * Retourne les classes permettant d'appliquer le style en fonction de si une plage est sélectionnée et en fonction de la durée de la plage.
	 */
	renseignerClassePlageDisponibleSelectionneEtHauteur(): string {
		const classe = this.estSelectionnee() ? 'plage-selectionnee ' : '';

		if (this.evenementsCalendriers().length === 1 && this.evenementsCalendriers()[0].duree.code !== Duree.JOURNEE) {
			return classe + 'demi-journee';
		}

		return classe + 'journee-entiere';
	}

	/**
	 * Permet de retourner les classes pour le positionnement des évènements dans la journée.
	 */
	determinerClasseSelonNombreElementEtDuree(): string {
		if (this.evenementsCalendriers().length === 0 || this.evenementsCalendriers()[0].duree.code === Duree.JOURNEE || this.evenementsCalendriers().length === 2) {
			return 'journee-entiere';
		}

		return this.evenementsCalendriers()[0].duree.code === Duree.MATIN ? 'matin' : 'apres-midi';
	}

	/**
	 * Détermine les icones en fonction du type d'évènement calendrier.
	 * @param evenementCalendrier l'évènement calendrier concerné.
	 */
	determinerClasseIconeEvenement(evenementCalendrier: EvenementCalendrier): string {
		const evenement = evenementCalendrier as Evenement;

		if (evenement.getType() === TypeEvenementCalendrier.RESERVATION) {
			return 'pi-briefcase';
		}

		if (!evenement.estTeletravail()) {
			return evenement.typeEvenement.code === TypeEvenement.CONGE ? 'pi-sun' : 'pi-globe';
		}

		switch (evenement.statut.code) {
			case StatutEvenement.VALIDE: {
				return 'pi-check-circle';
			}
			case StatutEvenement.EN_ATTENTE: {
				return 'pi-clock';
			}
			case StatutEvenement.REFUSE: {
				return 'pi-ban';
			}
		}
	}

	/**
	 * Renseigne le label de l'évènement calendrier en fonction de son type.
	 * @param evenementCalendrier
	 */
	labelEvenementcalendrier(evenementCalendrier: EvenementCalendrier): string {
		if (evenementCalendrier.getType() === TypeEvenementCalendrier.RESERVATION) {
			return this.translateService.instant('ACCUEIL.LABEL_RESERVATION');
		}

		const evenement = evenementCalendrier as Evenement;

		if (evenement.estTeletravail()) {
			return `${this.translateService.instant('CALENDRIER_JOURS_TELETRAVAIL.TELETRAVAIL_LABEL')} ${evenement.statut.designation.toLowerCase()}`;
		}

		if (evenement.typeEvenement.code === TypeEvenement.CONGE) {
			return this.translateService.instant('ACCUEIL.LABEL_TYPE_EVENEMENT_CONGE');
		}

		return this.translateService.instant('ACCUEIL.LABEL_TYPE_EVENEMENT_TRAVAILLE_AILLEURS');
	}

	/**
	 * Détermine les classe de l'évènement en fonction de son statut, sa durée et son type.
	 * @param evenementCalendrier
	 */
	determinerClasseSelonStatutDureeTypeEvenement(evenementCalendrier: EvenementCalendrier): string {
		let classe = '';

		if (evenementCalendrier.getType() === TypeEvenementCalendrier.EVENEMENT) {
			const evenement = evenementCalendrier as Evenement;
			classe += `type-evenement-${evenement.typeEvenement.code}`;

			if (evenement.estTeletravail()) {
				classe += ` statut-${evenement.statut.code}`;
			}
		}
		else {
			classe += 'reservation';
		}

		return classe + ` duree-${evenementCalendrier.duree.code}`;
	}

	/**
	 * Notifie la demande d'ouverture de la modale pour le détail de l'évènement calendrier.
	 * @param evenementCalendrier
	 */
	notifierOuvertureDetailModale(evenementCalendrier: EvenementCalendrier): void {
		if (evenementCalendrier.getType() === TypeEvenementCalendrier.EVENEMENT) {
			this.notifierSelectionEvenement.emit(evenementCalendrier as Evenement);
		}
		else {
			this.notifierSelectionReservation.emit(evenementCalendrier as Reservation);
		}
	}

	protected readonly isNotNullOrUndefined = isNotNullOrUndefined;
	protected readonly TypeEvenementCalendrier = TypeEvenementCalendrier;
}
